import React, {useState, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {toLocalDateStr} from "../../../utils/dateHelpers";
import {Users, Clock, ChevronDown, ChevronUp} from "lucide-react";
import {SummaryStrip, ListLoader, thClass} from "../../../stock/StockUI";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
    Number(v || 0).toLocaleString("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2}) + " DH";

const fmtTime = (dt) => {
    if (!dt) return "—";
    try {
        return new Date(dt).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"});
    } catch {
        return "—";
    }
};

// Only the best tables are listed — ranked by revenue (TTC).
const TOP_TABLES = 5;

// ─── Shape badge ──────────────────────────────────────────────────────────────

const ShapeBadge = ({shape}) => {
    const {t} = useTranslation("activite");
    const map = {
        rectangle: {label: t("tableMetrics.shapes.rectangle"), cls: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"},
        circle: {label: t("tableMetrics.shapes.circle"), cls: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"},
        square: {label: t("tableMetrics.shapes.square"), cls: "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"},
    };
    const s = map[shape] || {label: shape || "—", cls: "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"};
    return (
        <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>
    );
};

// ─── Mobile row (one per table) ──────────────────────────────────────────────

const TableRow = ({t}) => {
    const {t: tr} = useTranslation("activite");
    const [open, setOpen] = useState(false);

    return (
        <div className="bg-white dark:bg-gray-800">
            {/* Header row — always visible */}
            <button
                className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => setOpen(o => !o)}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">{t.table_name || "—"}</span>
                    <ShapeBadge shape={t.shape}/>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 tabular-nums">
                    <span className="text-xs font-semibold text-primary-dark dark:text-primary">{t.orders_count ?? 0} {tr("tableMetrics.ordersAbbr")}</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{fmt(t.total_revenue)}</span>
                    {open
                        ? <ChevronUp size={14} className="text-gray-400"/>
                        : <ChevronDown size={14} className="text-gray-400"/>}
                </div>
            </button>

            {/* Expanded detail grid */}
            {open && (
                <div className="px-4 pb-4 grid grid-cols-2 gap-x-4 gap-y-3">
                    {[
                        {label: tr("tableMetrics.capacity"),
                            value: <span className="inline-flex items-center gap-1"><Users
                                size={11}/>{t.capacity || "—"}</span>
                        },
                        {label: tr("tableMetrics.revenueHt"), value: fmt(t.total_revenue_ht)},
                        {label: tr("tableMetrics.averagePrice"), value: fmt(t.average_order_value)},
                        {
                            label: tr("tableMetrics.discounts"),
                            value: Number(t.total_discounts) > 0
                                ? <span className="text-orange-500 font-medium">- {fmt(t.total_discounts)}</span>
                                : "—"
                        },
                        {
                            label: tr("tableMetrics.firstUse"),
                            value: <span className="inline-flex items-center gap-1"><Clock
                                size={10}/>{fmtTime(t.first_use)}</span>
                        },
                        {
                            label: tr("tableMetrics.lastUse"),
                            value: <span className="inline-flex items-center gap-1"><Clock
                                size={10}/>{fmtTime(t.last_use)}</span>
                        },
                    ].map((row, i) => (
                        <div key={i}>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">{row.label}</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 tabular-nums">{row.value}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const TableMetrics = ({_dateRange, _startTime, _endTime, isDarkMode = false, refreshKey = 0}) => {
    const {t} = useTranslation("activite");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!_dateRange?.[0]?.startDate || !_dateRange?.[0]?.endDate) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const account = await secureStorage.getItem("selectedAccount");
                if (!account?.id) return;

                const start = toLocalDateStr(_dateRange[0].startDate);
                const end = toLocalDateStr(_dateRange[0].endDate);
                const startTime = _startTime || "00:00";
                const endTime = _endTime || "23:59";

                const res = await api.get(
                    `/activite/table_metrics?startDate=${start}&endDate=${end}&startTime=${startTime}&endTime=${endTime}`,
                    {headers: {"X-account-id": account.id}}
                );
                setData(res.data);
            } catch (err) {
                console.error("TableMetrics fetch error:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [_dateRange, _startTime, _endTime, refreshKey]);

    const allTables = useMemo(() => data?.tables || [], [data]);
    const tables = useMemo(
        () => [...allTables]
            .sort((a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0))
            .slice(0, TOP_TABLES),
        [allTables]
    );

    const base = isDarkMode ? "text-gray-100 bg-gray-900" : "text-gray-900 bg-white";

    if (loading) {
        return <ListLoader label={t("tableMetrics.loading")}/>;
    }

    if (error) {
        return (
            <div className={`w-full py-8 flex items-center justify-center ${base}`}>
                <p className="text-sm text-red-400">{t("tableMetrics.loadError")}</p>
            </div>
        );
    }

    if (!data) return null;

    // Totals cover every table of the period, not only the top ones shown.
    const totalOrders = allTables.reduce((s, tbl) => s + (tbl.orders_count || 0), 0);
    const totalRevenue = allTables.reduce((s, tbl) => s + Number(tbl.total_revenue || 0), 0);
    const totalRevenueHt = allTables.reduce((s, tbl) => s + Number(tbl.total_revenue_ht || 0), 0);
    const totalDiscounts = allTables.reduce((s, tbl) => s + Number(tbl.total_discounts || 0), 0);

    const tdClass = "px-6 py-4 whitespace-nowrap text-sm tabular-nums";

    return (
        <div className={`w-full text-sm ${base}`}>
            {/* ── Highlights : même bande à filets que le Stock ── */}
            <SummaryStrip items={[
                {
                    key: "mostUsed",
                    label: t("tableMetrics.mostUsedTable"),
                    value: data.most_used?.table_name || "—",
                    hint: data.most_used ? t("tableMetrics.ordersCount", {count: data.most_used.orders_count}) : null,
                },
                {
                    key: "topRevenue",
                    label: t("tableMetrics.topRevenue"),
                    value: data.top_revenue?.table_name || "—",
                    hint: data.top_revenue ? fmt(data.top_revenue.total_revenue) : null,
                    color: "text-primary-dark dark:text-primary",
                },
                {
                    key: "topRotation",
                    label: t("tableMetrics.topRotation"),
                    value: data.top_rotation?.table_name || "—",
                    hint: data.top_rotation
                        ? t("tableMetrics.rotationIndex", {value: Number(data.top_rotation.rotation_index || 0).toFixed(1)})
                        : null,
                },
            ]}/>

            {/* ── Empty state ── */}
            {tables.length === 0 && (
                <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 text-center">
                    <p className="text-gray-500 dark:text-gray-400">{t("tableMetrics.noData")}</p>
                </div>
            )}

            {tables.length > 0 && (
                <>
                    {/* Top N caption */}
                    {allTables.length > tables.length && (
                        <div className="px-4 sm:px-6 py-2 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                            {t("tableMetrics.topTables", {shown: tables.length, count: allTables.length})}
                        </div>
                    )}

                    {/* ── Mobile rows (< md) ── */}
                    <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
                        {tables.map((tbl, i) => (
                            <TableRow key={tbl.table_id || i} t={tbl}/>
                        ))}

                        {/* Mobile total row */}
                        {allTables.length > 1 && (
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                    {t("tableMetrics.totalTables", {count: allTables.length})}
                                </span>
                                <div className="text-right tabular-nums">
                                    <p className="text-xs font-semibold text-primary-dark dark:text-primary">
                                        {totalOrders} {t("tableMetrics.ordersAbbr")}
                                    </p>
                                    <p className="text-sm font-semibold">{fmt(totalRevenue)}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Desktop table (≥ md) ── */}
                    <div className="hidden md:block bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                                {[
                                    {label: t("tableMetrics.columns.table"), cls: ""},
                                    {label: t("tableMetrics.columns.shape"), cls: ""},
                                    {label: t("tableMetrics.columns.capacity"), cls: "text-center"},
                                    {label: t("tableMetrics.columns.orders"), cls: "text-right"},
                                    {label: t("tableMetrics.columns.revenueTtc"), cls: "text-right"},
                                    {label: t("tableMetrics.columns.revenueHt"), cls: "text-right"},
                                    {label: t("tableMetrics.columns.avgPrice"), cls: "text-right"},
                                    {label: t("tableMetrics.columns.discounts"), cls: "text-right"},
                                    {label: t("tableMetrics.columns.firstUse"), cls: "text-center"},
                                    {label: t("tableMetrics.columns.lastUse"), cls: "text-center"},
                                ].map((h, i) => (
                                    <th key={i} className={`${thClass} whitespace-nowrap ${h.cls}`}>{h.label}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {tables.map((tbl, i) => (
                                <tr key={tbl.table_id || i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className={`${tdClass} font-medium text-gray-900 dark:text-gray-100`}>{tbl.table_name || "—"}</td>
                                    <td className={tdClass}><ShapeBadge shape={tbl.shape}/></td>
                                    <td className={`${tdClass} text-center text-gray-700 dark:text-gray-300`}>
                                        <span className="inline-flex items-center gap-1">
                                            <Users size={12} className="text-gray-400"/>
                                            {tbl.capacity || "—"}
                                        </span>
                                    </td>
                                    <td className={`${tdClass} text-right text-gray-700 dark:text-gray-300`}>{tbl.orders_count ?? 0}</td>
                                    <td className={`${tdClass} text-right font-semibold text-gray-900 dark:text-gray-100`}>{fmt(tbl.total_revenue)}</td>
                                    <td className={`${tdClass} text-right text-gray-500 dark:text-gray-400`}>{fmt(tbl.total_revenue_ht)}</td>
                                    <td className={`${tdClass} text-right text-gray-700 dark:text-gray-300`}>{fmt(tbl.average_order_value)}</td>
                                    <td className={`${tdClass} text-right ${
                                        Number(tbl.total_discounts) > 0 ? "text-orange-500 font-medium" : "text-gray-400"
                                    }`}>
                                        {Number(tbl.total_discounts) > 0 ? `- ${fmt(tbl.total_discounts)}` : "—"}
                                    </td>
                                    <td className={`${tdClass} text-center text-gray-500 dark:text-gray-400`}>
                                        <span className="inline-flex items-center gap-1"><Clock size={12}/>{fmtTime(tbl.first_use)}</span>
                                    </td>
                                    <td className={`${tdClass} text-center text-gray-500 dark:text-gray-400`}>
                                        <span className="inline-flex items-center gap-1"><Clock size={12}/>{fmtTime(tbl.last_use)}</span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>

                            {/* Footer totals — all tables of the period */}
                            {allTables.length > 1 && (
                                <tfoot>
                                <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 font-semibold text-gray-700 dark:text-gray-200">
                                    <td className={tdClass} colSpan={3}>
                                        {t("tableMetrics.totalTablesParens", {count: allTables.length})}
                                    </td>
                                    <td className={`${tdClass} text-right`}>{totalOrders}</td>
                                    <td className={`${tdClass} text-right font-bold text-primary-dark dark:text-primary`}>{fmt(totalRevenue)}</td>
                                    <td className={`${tdClass} text-right`}>{fmt(totalRevenueHt)}</td>
                                    <td className={`${tdClass} text-right`} colSpan={2}>
                                        {totalDiscounts > 0 ? `- ${fmt(totalDiscounts)}` : "—"}
                                    </td>
                                    <td colSpan={2}/>
                                </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default TableMetrics;
